/**
 * Yup validators for Dutch data formats
 * These create reusable validation rules that can be used in form schemas
 */

import * as Yup from 'yup'
import { NL_FORMATS, INTERNATIONAL_FORMATS } from './formats'
import { normalizeNLPostalCode, normalizeBTWID, normalizeKVKNumber, normalizeVATNumber } from './normalizers'

/**
 * Create a Yup validator for Dutch postal codes
 * Auto-normalizes input (removes spaces, uppercase)
 *
 * @param message - Error message to show when validation fails
 * @returns Yup string schema with postal code validation
 *
 * @example
 * const schema = Yup.object({ postal_code: createNLPostalCodeValidator('Invalid postal code') })
 */
export const createNLPostalCodeValidator = (message: string) => {
  return Yup.string()
    .transform(normalizeNLPostalCode)
    .test('nl-postal-code', message, (value) => {
      if (!value) return true // Allow empty (use .required() separately)
      return NL_FORMATS.POSTAL_CODE.test(value)
    })
}

/**
 * Create a Yup validator for Dutch KVK (Chamber of Commerce) numbers
 * Auto-normalizes input (removes all non-digit characters)
 *
 * @param message - Error message to show when validation fails
 * @returns Yup string schema with KVK validation
 *
 * @example
 * const schema = Yup.object({ coc: createNLKVKValidator('Invalid KVK number') })
 */
export const createNLKVKValidator = (message: string) => {
  return Yup.string()
    .transform(normalizeKVKNumber)
    .test('nl-kvk', message, (value) => {
      if (!value) return true
      return NL_FORMATS.KVK_NUMBER.test(value)
    })
}

/**
 * Create a Yup validator for Dutch BTW-ID (VAT identification numbers)
 * Auto-normalizes input (removes spaces, uppercase, adds NL prefix if needed)
 *
 * @param message - Error message to show when validation fails
 * @returns Yup string schema with BTW-ID validation
 *
 * @example
 * const schema = Yup.object({ tax: createNLBTWIDValidator('Invalid BTW-ID') })
 */
export const createNLBTWIDValidator = (message: string) => {
  return Yup.string()
    .transform(normalizeBTWID)
    .test('nl-btw-id', message, (value) => {
      if (!value) return true

      // Check basic format (lenient - accepts NL + 12 alphanumeric/+/*)
      if (!NL_FORMATS.BTW_ID_BASIC.test(value)) {
        return false
      }

      // TODO: Optional - Add VIES API validation for real-time checking
      // This would verify the number exists in the EU database

      return true
    })
}

/**
 * Create a Yup validator for international VAT numbers
 * Auto-normalizes input (removes spaces, uppercase)
 * Performs basic format check only
 *
 * @param message - Error message to show when validation fails
 * @returns Yup string schema with international VAT validation
 *
 * @example
 * const schema = Yup.object({ tax: createInternationalVATValidator('Invalid VAT number') })
 */
export const createInternationalVATValidator = (message: string) => {
  return Yup.string()
    .transform(normalizeVATNumber)
    .test('international-vat', message, (value) => {
      if (!value) return true

      // Basic format check: 2-letter country code + 2-12 alphanumeric
      if (!INTERNATIONAL_FORMATS.VAT_NUMBER.test(value)) {
        return false
      }

      // TODO: Add library-based validation (jsvat) for offline format checking
      // TODO: Add VIES API validation for online verification

      return true
    })
}
